/**
 * Android hardware back-button coordination.
 *
 * Capacitor's `App` plugin overrides the default Android back behaviour as soon
 * as a `backButton` listener is registered, so we keep a single root listener
 * (see use-android-back-button.ts) that consults a stack of handlers. The most
 * recently registered handler runs first; if it consumes the event (returns
 * true) navigation stops there. Otherwise we fall back to in-app history /
 * exiting the app.
 *
 * This is what makes "Back" predictable inside messaging: opening a thread is
 * component state (not a route), so without this the hardware back button would
 * pop the router history and jump to an unrelated module instead of returning
 * to the thread list.
 */

export type BackHandler = () => boolean

const handlers: BackHandler[] = []

export function pushBackHandler(handler: BackHandler): () => void {
  handlers.push(handler)
  return () => {
    const index = handlers.indexOf(handler)
    if (index >= 0) handlers.splice(index, 1)
  }
}

/** Run handlers most-recent-first; returns true once one consumes the event. */
export function runBackHandlers(): boolean {
  for (let i = handlers.length - 1; i >= 0; i--) {
    try {
      if (handlers[i]()) return true
    } catch {
      /* a misbehaving handler must not break back navigation */
    }
  }
  return false
}
